import React, { useState } from "react";
import { ViewColumn } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";

// Column picker for the data tables: the user ticks the columns they want and
// applies them, so the table only re-renders once the selection is confirmed.
const ColumnToggle = ({ table, columnGroups }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [draft, setDraft] = useState({});

  const groups = (Array.isArray(columnGroups) ? columnGroups : []).filter(
    (group) => group?.label && Array.isArray(group?.columns)
  );
  const groupedColumnIds = new Set(groups.flatMap((group) => group.columns));

  // A column belongs in the picker when it is a real data column the table
  // allows hiding, has a label to show, and is not covered by a group entry.
  // Columns rendered without a visible heading supply their own picker label.
  const pickerItems = [
    ...table
      .getAllLeafColumns()
      .filter(
        (column) =>
          column?.id !== "mrt-row-select" &&
          !column?.columnDef?.enableHiding &&
          !groupedColumnIds.has(column.id) &&
          Boolean(column?.columnDef?.columnLabel || column?.columnDef?.header)
      )
      .map((column) => ({
        key: column.id,
        label: column.columnDef.columnLabel || column.columnDef.header,
        columnIds: [column.id],
      })),
    ...groups.map((group) => ({
      key: `group:${group.label}`,
      label: group.label,
      columnIds: group.columns.filter((colId) => table.getColumn(colId)),
    })),
  ].filter((item) => item.columnIds.length > 0);

  const appliedVisibility = () => {
    const visibility = {};
    pickerItems.forEach((item) => {
      item.columnIds.forEach((colId) => {
        visibility[colId] = Boolean(table.getColumn(colId)?.getIsVisible());
      });
    });
    return visibility;
  };

  const handleOpen = (event) => {
    setDraft(appliedVisibility());
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const setItems = (items, checked) => {
    setDraft((current) => {
      const next = { ...current };
      items.forEach((item) => {
        item.columnIds.forEach((colId) => {
          next[colId] = checked;
        });
      });
      return next;
    });
  };

  const handleApply = () => {
    table.setColumnVisibility((current) => ({ ...current, ...draft }));
    handleClose();
  };

  const itemState = (item) => {
    const selected = item.columnIds.filter((colId) => draft[colId]).length;
    return {
      checked: selected === item.columnIds.length,
      indeterminate: selected > 0 && selected < item.columnIds.length,
    };
  };

  const applied = appliedVisibility();
  const isDirty = Object.keys(applied).some(
    (colId) => Boolean(applied[colId]) !== Boolean(draft[colId])
  );
  const visibleCount = pickerItems.filter(
    (item) => itemState(item).checked
  ).length;

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Show/Hide Columns">
        <Button
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleOpen}
          className="d-flex align-items-center"
        >
          <ViewColumn style={{ color: "grey" }} />
          <span className="ml-2">Columns</span>
        </Button>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            elevation: 4,
            sx: { borderRadius: "8px", width: 460, maxWidth: "90vw" },
          },
        }}
      >
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Column settings
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {visibleCount} of {pickerItems.length} columns selected
          </Typography>
        </Box>
        <Divider />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            columnGap: 1,
            px: 2,
            py: 1,
            maxHeight: 340,
            overflowY: "auto",
          }}
        >
          {pickerItems.map((item) => {
            const { checked, indeterminate } = itemState(item);
            return (
              <FormControlLabel
                key={item.key}
                sx={{ m: 0, py: 0.25 }}
                control={
                  <Checkbox
                    size="small"
                    color="primary"
                    checked={checked}
                    indeterminate={indeterminate}
                    onChange={(_event, isChecked) =>
                      setItems([item], isChecked)
                    }
                  />
                }
                label={<Typography variant="body2">{item.label}</Typography>}
              />
            );
          })}
        </Box>
        <Divider />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
          }}
        >
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small" onClick={() => setItems(pickerItems, true)}>
              Select all
            </Button>
            <Button size="small" onClick={() => setItems(pickerItems, false)}>
              Clear all
            </Button>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small" color="inherit" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="secondary"
              disabled={!isDirty}
              onClick={handleApply}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default ColumnToggle;
